import React, { useState, useRef, useEffect } from 'react';
import { COUNTRIES, DEFAULT_COUNTRY } from '../../config/countries';

interface CountryCodeDropdownProps {
    value: string | number;                    // Current countryId value
    onChange: (countryId: number) => void;     // Callback when country changes
    disabled?: boolean;                        // Optional disabled state
    className?: string;                        // Optional additional CSS classes
    showDialingCode?: boolean;                 // Show dialing code in dropdown (default: true)
}

/**
 * CountryCodeDropdown Component
 *
 * A reusable dropdown component for selecting countries with their dialing codes and CountryIds.
 * This component ensures that only valid CountryIds are sent to the SSM API.
 *
 * Usage:
 * <CountryCodeDropdown
 *   value={tenantDetails.countryId}
 *   onChange={(countryId) => setTenantDetails({ ...tenantDetails, countryId: countryId.toString() })}
 *   disabled={isSubmitting}
 * />
 */
const CountryCodeDropdown: React.FC<CountryCodeDropdownProps> = ({
    value,
    onChange,
    disabled = false,
    className = '',
    showDialingCode = true
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Convert value to number for comparison
    const currentCountryId = typeof value === 'string' ? parseInt(value) || DEFAULT_COUNTRY.countryId : value;

    // Find current country or use default
    const currentCountry = COUNTRIES.find(c => c.countryId === currentCountryId) || DEFAULT_COUNTRY;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (countryId: number) => {
        onChange(countryId);
        setIsOpen(false);
    };

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className={`country-code-dropdown ${className}`} ref={dropdownRef}>
            <div className="form-floating">
                <div
                    className={`form-select ${disabled ? 'disabled' : ''}`}
                    onClick={toggleDropdown}
                    style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                    {showDialingCode
                        ? `${currentCountry.dialingCode} - ${currentCountry.name}`
                        : currentCountry.name
                    }
                </div>
                <label htmlFor="countryId">
                    <i className="bi bi-globe text-primary me-2"></i>
                    Country
                </label>

                {isOpen && (
                    <div className="custom-dropdown-menu">
                        {COUNTRIES.map((country) => (
                            <div
                                key={country.code}
                                className={`dropdown-option ${country.countryId === currentCountryId ? 'selected' : ''}`}
                                onClick={() => handleSelect(country.countryId)}
                            >
                                {showDialingCode
                                    ? `${country.dialingCode} - ${country.name}`
                                    : country.name
                                }
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <style>{`
                .country-code-dropdown {
                    position: relative;
                }
                .country-code-dropdown .form-select {
                    cursor: pointer;
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
                    background-repeat: no-repeat;
                    background-position: right 0.75rem center;
                    background-size: 16px 12px;
                }
                .country-code-dropdown .form-select.disabled {
                    cursor: not-allowed;
                    opacity: 0.65;
                }
                .country-code-dropdown .custom-dropdown-menu {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    z-index: 1050;
                    background: white;
                    border: 1px solid #dee2e6;
                    border-radius: 0.25rem;
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
                    margin-top: 0.25rem;
                    max-height: 180px;
                    overflow-y: auto;
                }
                .country-code-dropdown .dropdown-option {
                    padding: 0.5rem 0.75rem;
                    cursor: pointer;
                    transition: background-color 0.15s ease-in-out;
                }
                .country-code-dropdown .dropdown-option:hover {
                    background-color: #f8f9fa;
                }
                .country-code-dropdown .dropdown-option.selected {
                    background-color: #0d6efd;
                    color: white;
                }
            `}</style>
        </div>
    );
};

export default CountryCodeDropdown;
